import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Field, FieldArray } from 'redux-form';
import { normalizeWeight } from '../../../../utils/conversionFunctions';
import CustomVariablesForm from '../../../../components/CustomVariables/CustomVariablesForm';
import { renderComboBox, renderField, renderToggleSwitch, textarea } from '../../../../components/FormFields/reduxFormRenderers';
import OfferProperties from './OfferProperties';
import CustomPropertiesSetup from '../../../../components/CustomProperties/CustomProperties/CustomPropertiesSetup';
import { containerNames, layoutNames, offerTypeNames } from '../constants';
import { filterContainersByOfferType, SiteField } from '../sequenceUtils';
import './RightAside.css';

const LABEL_SX = { display: 'block', mb: 0.5 };

/**
 * Panes stay mounted and hidden, the way reactstrap's TabPane did. They hold redux-form Fields,
 * and a Field that unmounts unregisters its value and its validation with it.
 */
const TabPanel = ({ value, tabId, children }) => (
  <Box role='tabpanel' hidden={value !== tabId}>
    {children}
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography component='span' sx={{ fontWeight: 600 }}>
    {children}
  </Typography>
);

const RightAside = React.memo(
  ({
    selectedElement,
    containers,
    layouts,
    sites,
    isEditMode,
    targetReturningUsers,
    offerTypes,
    propertySetsIds,
    setPropertySetsIds,
    propertySetsValues,
    setPropertySetsValues,
    offerVariationsByPrimaryOfferId,
  }) => {
    const [tab, setTab] = useState('sequence');
    const [sequenceTab, setSequenceTab] = useState('properties');

    const handleTabChange = useCallback((newTab) => {
      setTab(newTab);
    }, []);

    const handleSequenceTabChange = useCallback((newSequenceTab) => {
      setSequenceTab(newSequenceTab);
    }, []);

    return (
      <div className='right-aside-container'>
        <Tabs value={tab} onChange={(_event, next) => handleTabChange(next)} variant='fullWidth'>
          <Tab value='offer' label='Offer' />
          <Tab value='sequence' label='Sequence' />
        </Tabs>
        <TabPanel value={tab} tabId='offer'>
          <OfferProperties
            selectedElement={selectedElement}
            containers={containers}
            layouts={layouts}
            offerVariationsByPrimaryOfferId={offerVariationsByPrimaryOfferId}
          />
        </TabPanel>
        <TabPanel value={tab} tabId='sequence'>
          <Tabs
            value={sequenceTab}
            onChange={(_event, next) => handleSequenceTabChange(next)}
            variant='fullWidth'
            sx={{ '& .MuiTab-root': { minWidth: 0, px: 1 } }}
          >
            <Tab value='properties' label='Properties' />
            <Tab value='design' label='Design' />
            <Tab value='customVariables' label='Custom Variables' />
          </Tabs>
          <Box id='sequenceSubSection'>
            <TabPanel value={sequenceTab} tabId='properties'>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* The Active toggle moved up into the page header, where every detail page keeps it. */}
                <SectionTitle>Properties</SectionTitle>
                <SiteField fieldName='site' sites={sites} disabled={isEditMode} />
                <SectionTitle>Flex &amp; Targeting</SectionTitle>
                <Stack direction='row' spacing={1.5} sx={{ alignItems: 'center' }}>
                  <FormLabel htmlFor='targetReturningUsers' sx={{ m: 0 }}>
                    Target Return Users
                  </FormLabel>
                  <Field name='targetReturningUsers' id='targetReturningUsers' component={renderToggleSwitch} />
                </Stack>
                <Grid container spacing={2}>
                  {targetReturningUsers && (
                    <Grid size={6}>
                      <Field
                        name={'returnVisits'}
                        normalize={normalizeWeight}
                        component={renderField}
                        type='number'
                        label='Return Visits'
                      />
                    </Grid>
                  )}
                  <Grid size={6}>
                    <Field
                      name={'flex'}
                      normalize={normalizeWeight}
                      component={renderField}
                      type='number'
                      label='Flex'
                    />
                  </Grid>
                  <Grid size={6}>
                    <Field
                      name={'priority'}
                      normalize={normalizeWeight}
                      component={renderField}
                      type='number'
                      label='Priority'
                    />
                  </Grid>
                </Grid>
                <SectionTitle>Sequence Description</SectionTitle>
                <Field name={'sequenceDescription'} component={textarea} />
                <CustomPropertiesSetup
                  propertySetsIds={propertySetsIds}
                  setPropertySetsIds={setPropertySetsIds}
                  propertySetsValues={propertySetsValues}
                  setPropertySetsValues={setPropertySetsValues}
                  type={'modal'}
                />
              </Stack>
            </TabPanel>
            <TabPanel value={sequenceTab} tabId='design'>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <SectionTitle>Default Layouts</SectionTitle>
                {layoutNames.map((layoutName, i) => (
                  <div key={layoutName}>
                    <FormLabel htmlFor={layoutName} sx={LABEL_SX}>
                      {offerTypeNames[i]}
                    </FormLabel>
                    <Field
                      name={layoutName}
                      id={layoutName}
                      component={renderComboBox}
                      data={layouts}
                      valueField='id'
                      textField='name'
                      link='/content/layouts'
                    />
                  </div>
                ))}
                <SectionTitle>Default Containers</SectionTitle>
                {containerNames.map((containerName, i) => {
                  const offerTypeName = offerTypeNames[i];
                  const filteredContainers = filterContainersByOfferType(containers, offerTypes[offerTypeName]);
                  return (
                    <div key={containerName}>
                      <FormLabel htmlFor={containerName} sx={LABEL_SX}>
                        {offerTypeNames[i]}
                      </FormLabel>
                      <Field
                        name={containerName}
                        id={containerName}
                        component={renderComboBox}
                        data={filteredContainers}
                        valueField='id'
                        textField='name'
                        link='/content/containers'
                      />
                    </div>
                  );
                })}
              </Stack>
            </TabPanel>
            <TabPanel value={sequenceTab} tabId='customVariables'>
              <FieldArray name='customVariables' component={CustomVariablesForm} customVarFlag='SUD_' />
            </TabPanel>
          </Box>
        </TabPanel>
      </div>
    );
  }
);

RightAside.displayName = 'RightAside';

export default RightAside;
